import {
  type DataTypeKey,
  FtmEntityPersonOption,
  FtmEntityV2,
  LinkRelationType,
  type PrimitiveTypes,
  type SemanticSubTypeField,
  type SemanticSubTypeFieldMap,
  type SemanticTypeField,
  semanticTypesByDataType,
  type TableModel,
} from '@app-builder/models';
import type { CountryCodeFormat, CurrencyCodeFormat, EnumEntry } from '@app-builder/models/enum';
import { FtmEntity } from 'marble-api';
import { match, P } from 'ts-pattern';

export type LinkValue = {
  linkId: string;
  name: string;
  sourceTableId: string;
  tableFieldId: string;
  relationType: LinkRelationType;
  targetTableId: string;
  isNew?: boolean;
};

export type RawLink = {
  id: string;
  name: string;
  parent_table_name: string;
  parent_table_id: string;
  parent_field_name: string;
  parent_field_id: string;
  child_table_name: string;
  child_table_id: string;
  child_field_name: string;
  child_field_id: string;
};

export type RawField = {
  id: string;
  name: string;
  description: string;
  data_type: PrimitiveTypes;
  table_id: string;
  is_enum?: boolean;
  nullable?: boolean;
  values?: (string | number)[];
  unicity_constraint?: string;
  ftm_property?: string;
};

export { type EnumColors, enumColors } from '@app-builder/models/enum';

export const semanticTypeTable = ['person', 'company', 'account', 'transaction', 'event', 'partner', 'other'] as const;
export type SemanticTypeTable = (typeof semanticTypeTable)[number];

export type TableField = {
  id: string;
  name: string;
  description: string;
  dataType: PrimitiveTypes;
  tableId: string;
  isEnum: boolean;
  nullable: boolean;
  alias: string;
  hidden: boolean;
  unicityConstraint: string;
  ftmProperty?: string;
  semanticType: SemanticTypeField;
  semanticSubType?: SemanticSubTypeField;
  currencyExponent?: number;
  decimalPrecision?: number;
  currencyFieldId?: string;
  booleanDisplay?: 'yes_no' | 'checkbox';
  isInteger?: boolean;
  foreignkeyTable?: string;
  isDefaultBelongsTo?: boolean;
  enumValues?: EnumEntry[];
  countryCodeFormat?: CountryCodeFormat;
  currencyCodeFormat?: CurrencyCodeFormat;
  isNew: boolean;
  locked?: boolean;
};

export function getSemanticSubOptions(
  dataType: DataTypeKey,
  semanticType: SemanticTypeField,
): { value: SemanticSubTypeField }[] | undefined {
  const options = semanticTypesByDataType[dataType];
  if (!options) return undefined;
  const match = options.find((o) => o.value === semanticType);
  return match && 'subOptions' in match ? match.subOptions : undefined;
}

export type SemanticTableFormValues = {
  tableId: string;
  name: string;
  alias: string;
  entityType: FtmEntityV2 | 'unset';
  subEntity: FtmEntityPersonOption | 'unset';
  belongsToTableId: string;
  fields: TableField[];
  mainTimestampFieldName: string;
  links: LinkValue[];
  metaData: Record<string, unknown>;
  isCanceled: boolean;
  isVisited: boolean;
  ftmEntity?: FtmEntity;
};

export type SemanticTableChangedProperty = Exclude<keyof SemanticTableFormValues, 'fields' | 'links' | 'isVisited'>;

export type ChangeRecord =
  | { type: 'table'; operation: 'MOD'; changedProperties: SemanticTableChangedProperty[] }
  | { type: 'field'; operation: 'MOD' | 'DEL'; objectId: string }
  | { type: 'field'; operation: 'ADD'; objectName: string }
  | { type: 'link'; operation: 'DEL'; objectId: string }
  | { type: 'link'; operation: 'MOD'; objectId: string; relationshipType: LinkRelationType }
  | { type: 'link'; operation: 'ADD'; objectName: string };

export function getMockValue({
  dataType,
  semanticType,
  semanticSubType,
  isEnum,
  enumValues,
  countryCodeFormat,
  currencyCodeFormat,
}: Pick<
  TableField,
  'dataType' | 'semanticType' | 'semanticSubType' | 'isEnum' | 'enumValues' | 'countryCodeFormat' | 'currencyCodeFormat'
>) {
  try {
    if ((dataType === 'Int' || dataType === 'Float') && (isEnum || semanticType === 'enum')) return 123;
    if (dataType === 'Coords') return '48.8566, 2.3522';
    if (dataType === 'IpAddress') return '127.0.0.1';
    if (dataType === 'Bool') return true;
    if (!semanticType) {
      return match(dataType)
        .with('String', () => 'Welcome to Marble')
        .with('Timestamp', () => '2021-01-01T14:20:00.000Z')
        .with(P.union('Int', 'Float'), () => 12345678)
        .otherwise(() => 'unexpected value');
    }
    const value = match(semanticType)
      .with('text', () => 'Welcome to Marble')
      .with('name', () =>
        semanticSubType
          ? match(semanticSubType as SemanticSubTypeFieldMap['name'])
              .with('caption', () => 'Company name or John Doe Jr')
              .with('first_name', () => 'John')
              .with('last_name', () => 'Doe')
              .with('middle_name', () => 'Jr')
              .otherwise(() => 'unexpected value')
          : 'John Doe Jr',
      )
      .with('enum', () =>
        semanticSubType
          ? match(semanticSubType as SemanticSubTypeFieldMap['enum'])
              .with('currency', () => (currencyCodeFormat === 'Number' ? '978' : 'EUR'))
              .with('country', () => (countryCodeFormat === 'alpha3' ? 'FRA' : 'FR'))
              .with('key_color_value', () => enumValues?.at(-1)?.key ?? '')
              .with('mcc_code', () => '5411')
              .with('autocomplete', () => 'Autocompleted value')
              .otherwise(() => 'unexpected value')
          : 'Enum value',
      )
      .with('currency_code', () => (currencyCodeFormat === 'Number' ? '978' : 'EUR'))
      .with('foreign_key', () => 'ForeignKey')
      .with('country', () => (countryCodeFormat === 'alpha3' ? 'FRA' : 'FR'))
      .with('address', () => '123 Main St, Anytown, USA')
      .with('unique_id', () =>
        semanticSubType
          ? match(semanticSubType as SemanticSubTypeFieldMap['unique_id'])
              .with('registration_number', () => 'REG1234567890')
              .with('tax_id', () => 'TAX1234567890')
              .with('opaque_id', () => '58e6908a-4eab-4985-8ebe-00b2f6900507')
              .otherwise(() => 'unexpected value')
          : 'Unique ID value',
      )
      .with('link', () =>
        semanticSubType
          ? match(semanticSubType as SemanticSubTypeFieldMap['link'])
              .with('url', () => 'https://www.google.com')
              .with('email', () => 'john.doe@example.com')
              .with('phone', () => '+33612345678')
              .otherwise(() => 'unexpected value')
          : 'Link value',
      )
      .with('account_identifier', () =>
        semanticSubType
          ? match(semanticSubType as SemanticSubTypeFieldMap['account_identifier'])
              .with('account_number', () => '12345678901234567890')
              .with('iban', () => 'FR7612345678901234567890123')
              .with('bic', () => 'TRZFR32AXXX')
              .otherwise(() => 'unexpected value')
          : 'Account identifier value',
      )
      .with('timestamp', () => '2021-01-01T14:20:00.000Z')
      .with('date_of_birth', () => '1990-01-01')
      .with('last_update', () => '2021-01-01T14:20:00.000Z')
      .with('creation_date', () => '2021-01-01T14:20:00.000Z')
      .with('deletion_date', () => '2021-01-01T14:20:00.000Z')
      .with('initiation_date', () => '2021-01-01T14:20:00.000Z')
      .with('validation_date', () => '2021-01-01T14:20:00.000Z')
      .with('number', () => 12345.6789)
      .with('monetary_amount', () => 1234567890)
      .with('percentage', () => 0.34)
      .otherwise(() => 'unknown value');
    return value;
  } catch {
    return 'unexpected value';
  }
}

// Returns true if a table can serve as the target of a link (transaction/event/account "belongs to" link).
export function isLinkableTable(table: TableModel): boolean {
  return table.semanticType === 'person';
}
